import React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { hexToRgba } from '../../../features/src/color-conversion';
import {
  spacer16,
  spacer24,
  spacer40,
  spacer80,
  cssColorValue,
  white,
  black,
  screenSmMax,
  gold155,
  Type,
  spacer56,
  createBasicColorSet,
} from '@spotify-internal/encore-web-v5';
import { ServiceTile } from './ServiceTile';
import { Box, ButtonPrimaryAsLink, Link } from '../ui';
import { lightTheme } from '../Theme';
import { useT } from '../../../features/src/i18n';
import { TRACK_CAT } from './constants';
import type { ProvidersDirectoryType } from './types';

export function useDescription() {
  const t = useT();
  return t(
    'FRODOR_PROVIDER_DESCRIPTION',
    'Here you’ll find a list of our preferred and recommended providers, all of which meet our standards for delivering music to our platform.',
    'Describes the content and purpose of the provider page.',
  );
}

export const Disclaimer = styled(Type.p)`
  font-style: italic;
  margin: ${spacer40} auto;
  padding-top: ${spacer16};
  border-top: 1px solid ${cssColorValue('decorativeSubdued')};
`;

export const PageContainer = styled.div``;

export const ContentBox = styled(Box)``;

export const Banner = styled(Box)`
  margin: auto;
  padding-top: ${spacer56};
  padding-bottom: ${spacer56};
  background: ${black};
  &,
  a {
    color: ${props => props.theme.banner.text};
  }
  a:hover,
  a:focus:not(:disabled) {
    color: ${props => hexToRgba(props.theme.banner.text, 0.75)};
  }

  div {
    max-width: ${screenSmMax};
    margin-left: auto;
    margin-right: auto;
  }
`;

const BannerHeader = styled(Type.h1).attrs({
  paddingBottom: '0.67em',
  variant: 'forte',
})`
  padding-bottom: ${spacer24};
`;

const BannerDescription = styled(Type.p).attrs({
  variant: 'ballad',
})`
  padding-bottom: ${spacer24};
`;

const LearnMoreLink = styled(Link)`
  text-decoration: underline;
  color: ${white};
`;

export const PageContent = styled.div`
  max-width: ${screenSmMax};
  margin-bottom: ${spacer40};
  margin-top: ${spacer24};
  margin-left: auto;
  margin-right: auto;

  p {
    margin-bottom: ${spacer16};
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-bottom: ${spacer16};
    margin-top: ${spacer24};
  }
`;

const CategoryTitle = styled(Type).attrs({
  paddingBottom: '0.75em',
  variant: 'alto',
})``;

const CategoryDescription = styled(Type.p).attrs({
  variant: 'ballad',
})`
  padding-top: ${spacer16};
`;

const CategoryContainer = styled.ul`
  list-style: none;
  padding-left: 0;
  padding-bottom: ${spacer80};
  li {
    margin-top: ${spacer16};
    padding: ${spacer40} 0;
    border-top: 1px solid ${cssColorValue('decorativeSubdued')};
  }
`;

const FooterBanner = styled.div`
  background: ${cssColorValue('decorativeSubdued')};
  color: ${black};
  width: 100%;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-around;
  min-height: 180px;
  align-items: center;
  padding: ${spacer16};

  div {
    max-width: ${screenSmMax};
    margin-left: auto;
    margin-right: auto;
    display: flex;
    justify-content: space-between;
    align-items: center;

    @media (max-width: ${screenSmMax}) {
      flex-direction: column;
      justify-content: center;
      min-height: auto;

      h3 {
        padding-bottom: ${spacer24};
      }
    }
  }

  h3 {
    margin: 0;
    padding-right: ${spacer24};
  }
  a,
  a:hover,
  a:focus:not(:disabled),
  a:active {
    color: ${white};
  }
`;

const BannerButton = styled(ButtonPrimaryAsLink)``;

export const ProviderDirectory: React.FC<ProvidersDirectoryType> = ({
  artistDistributors = null,
  labelDistributors = null,
  deliveryPlatforms = null,
}) => {
  const t = useT();
  const DESCRIPTION = useDescription();
  return (
    <ThemeProvider theme={lightTheme}>
      <PageContainer>
        <Banner>
          <div>
            <BannerHeader>
              {t(
                'FRODOR_PROVIDER_DIRECTORY_PAGE_HEADER',
                'Provider Directory',
                'Page header text',
              )}
            </BannerHeader>

            <BannerDescription>
              {t(
                'FRODOR_PROVIDER_DIRECTORY_DESCRIPTION',
                '{description} ',
                'Description of the providers directory',
                { description: DESCRIPTION },
              )}
            </BannerDescription>
            <LearnMoreLink
              href="/help/article/getting-music-on-spotify"
              track={[TRACK_CAT, 'Learn More']}
            >
              {t(
                'FRODOR_PROVIDER_DIRECTORY_LEARN_MORE',
                'Learn more',
                'To learn more about the provider directory click this link',
              )}
            </LearnMoreLink>
          </div>
        </Banner>
        <ContentBox>
          <PageContent>
            <CategoryTitle>
              {t(
                'FRODOR_PROVIDER_DIRECTORY_ARTIST_DISTRIBUTORS',
                'Artist distributors',
                'Title text',
              )}
            </CategoryTitle>
            <CategoryDescription>
              {t(
                'FRODOR_PROVIDER_DIRECTORY_ARTIST_DISTRIBUTORS_DESCRIPTION',
                `Want to get your music on Spotify? These companies will handle the licensing and distribution to Spotify and other streaming services, and pay you the royalties you earn when listeners stream. These providers offer instant access to Spotify For Artists for all their users.`,
                'Description text',
              )}
            </CategoryDescription>

            <CategoryContainer>
              {artistDistributors &&
                artistDistributors.map(svc => (
                  <ServiceTile key={svc.id} {...svc} />
                ))}
            </CategoryContainer>

            <CategoryTitle>
              {t(
                'FRODOR_PROVIDER_DIRECTORY_LABEL_DIST_TITLE',
                'Label distributors',
                'Title text',
              )}
            </CategoryTitle>
            <CategoryDescription>
              {t(
                'FRODOR_PROVIDER_DIRECTORY_LABEL_DIST_DESC',
                `Work at a label and want to get your music on Spotify? These companies will handle the licensing and distribution to Spotify and other streaming services, and pay you the royalties you earn when listeners stream.`,
                'Description text',
              )}
            </CategoryDescription>

            <CategoryContainer>
              {labelDistributors &&
                labelDistributors.map(svc => (
                  <ServiceTile key={svc.id} {...svc} />
                ))}
            </CategoryContainer>

            <CategoryTitle>
              {t(
                'FRODOR_PROVIDER_DIRECTORY_DELIVERY_TITLE',
                'Delivery platforms',
                'Title text',
              )}
            </CategoryTitle>
            <CategoryDescription>
              {t(
                'FRODOR_PROVIDER_DIRECTORY_DELIVERY_DESC',
                `Don’t need help with the licensing side of things? These companies will get your music and metadata where it needs to go.`,
                'Title text',
              )}
            </CategoryDescription>

            <CategoryContainer>
              {deliveryPlatforms &&
                deliveryPlatforms.map(svc => (
                  <ServiceTile key={svc.id} {...svc} />
                ))}
            </CategoryContainer>
          </PageContent>

          <Disclaimer>
            {t(
              'FRODOR_PROVIDER_DIRECTORY_DISCLAIMER',
              `Our investment in any provider does not affect who is featured on this page, nor does it affect any decisions about how content is treated on Spotify's service.`,
              'Legal disclaimer for this page',
            )}
          </Disclaimer>
        </ContentBox>
        <FooterBanner>
          <div>
            <h3>
              {t(
                'FRODOR_PROVIDER_DIRECTORY_HOW_TO_IMPROVE',
                `What do you want us to include as a part of our services directory?`,
                'Question to user',
              )}
            </h3>
            <BannerButton
              external
              href="https://spotifyresearch.typeform.com/to/z1XDyg"
              track={[TRACK_CAT, 'Open Survey']}
              UNSAFE_colorSet={createBasicColorSet(gold155, white)}
            >
              {t(
                'FRODOR_PROVIDER_DIRECTORY_SUBMIT_IDEA',
                `Submit your idea`,
                'Submit idea call to action button text',
              )}
            </BannerButton>
          </div>
        </FooterBanner>
      </PageContainer>
    </ThemeProvider>
  );
};
